import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { formatPln } from '../catalog/money';
import { packlistRights, type ToPacklistResult } from '../quotes/GenerateMenu';
import { QUOTES_KEY, QUOTES_RESOURCE, quoteKey, type QuoteOfEvent, type VersionSummary } from '../quotes/types';
import { stateText } from '../quotes/VersionBar';
import { DialogLayer } from '../ui/Dialog';
import { markStyle, useContractorColors } from '../contractors/colors';
import type { RowAction } from '../ui/RowMenu';
import { SupplierLacksItem } from '../subrentals/SupplierLacksItem';
import { catalogTitle, type CatalogTarget } from '../nav/catalogLink';
import { CatalogRow } from '../nav/CatalogRow';
import { formatWindow, packlistKey, type PacklistVersion, type PacklistView, type SupplierLine, type SupplierSection, type VersionLine } from './types';

/**
 * Versions of the packlist and the suppliers' gear on it (owner 2026-10-22 14:29: "wersje sprzętówki tak samo niech są,
 * dopóki ich nie usunę"; "pozycje od dostawców z wyszczególnieniem tego po naszym sprzęcie"; 15:01: "w sprzętówce też
 * muszę móc zmienić dostawcę tak jak w kosztorysie").
 */

export type FromQuoteResult = ToPacklistResult & { packlistVersion: number; newVersion: boolean; alreadyInSync?: boolean };

/** The quote version a packlist is made from by default: the accepted one, else the newest not rejected (else the newest). */
export function defaultQuoteVersion(versions: readonly VersionSummary[]): VersionSummary | null {
  const accepted = versions.find((v) => v.state === 'zaakceptowana');
  if (accepted) return accepted;
  const sorted = [...versions].sort((a, b) => b.number - a.number);
  return sorted.find((v) => v.state !== 'odrzucona') ?? sorted[0] ?? null;
}

/** "Utworzono wersję v3 sprzętówki z kosztorysu v2 (poprzednia zostaje w wersjach): dodano 1, zmieniono 2." */
export function fromQuoteText(r: FromQuoteResult, quoteNumber: number): string {
  // nothing would change (the same parts and pickups): no new version (review)
  if (r.alreadyInSync) return `Sprzętówka jest już zgodna z kosztorysem (v${r.packlistVersion}).`;
  const parts = [`dodano ${r.added}`, `zmieniono ${r.changed}`];
  if (r.removed > 0) parts.push(`usunięto ${r.removed}`);
  if (r.skipped.length > 0) parts.push(`pominięto ${r.skipped.length} (usługi i pozycje własne)`);
  return r.newVersion
    ? `Utworzono wersję v${r.packlistVersion} sprzętówki z kosztorysu v${quoteNumber} (poprzednia zostaje w wersjach): ${parts.join(', ')}.`
    : `Sprzętówka z kosztorysu v${quoteNumber}: ${parts.join(', ')}.`;
}

const shortDate = (iso: string) => new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit' }).format(new Date(iso));

/** "v1 · v2 · v3" like the quote's versions, each with a short note; only when there is more than one. */
export function VersionSwitch({ versions, shown, onShow }: { versions: PacklistVersion[]; shown: number; onShow: (n: number) => void }) {
  if (versions.length < 2) return null;
  return (
    <div className="segment pl-versions" role="group" aria-label="Wersje sprzętówki" data-feature-id="packlists.versions">
      {versions.map((v) => (
        <button key={v.number} type="button" aria-pressed={v.number === shown} onClick={() => onShow(v.number)}>
          <span className="pl-vnum">v{v.number}</span>
          <span className="pl-vstate">{v.current ? 'aktualna' : v.label || (v.createdAt ? shortDate(v.createdAt) : 'starsza')}</span>
        </button>
      ))}
    </div>
  );
}

/** An older version: a plain read-only table (no reservations, no editing). */
export function OldVersionSheet({ lines, cols }: { lines: VersionLine[]; cols: { quantity: boolean; window: boolean; notes: boolean } }) {
  if (lines.length === 0) return <p className="muted">W tej wersji nie było naszego sprzętu.</p>;
  return (
    <table className="table pl-old-version">
      <thead>
        <tr>
          <th>Sprzęt</th>
          {cols.quantity && <th className="num">Ilość</th>}
          {cols.window && <th>Okno</th>}
          {cols.notes && <th>Uwagi</th>}
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id}>
            <td>{l.itemName}</td>
            {cols.quantity && <td className="num">{l.quantity ?? ''}</td>}
            {cols.window && <td>{formatWindow(l)}</td>}
            {cols.notes && <td>{l.notes ?? ''}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Whether the person may change a supplier here: as in the quote (edit quotes and rentals; the server checks the rest). */
export const mayMoveSupplier = (me: Me) => can(me, QUOTES_RESOURCE, 'edit') && can(me, 'subrentals.rentals', 'edit');

/**
 * One section per supplier, "Odbiór: <dostawca>" — only where there are no category groups: an older version (read
 * only) and the loading screen. The editor shows each supplier line in its category group (`SupplierLineRow`). With
 * `editable`, the supplier of a line from a quote is changed the quote's way (the same rule, the same messages).
 */
export function SupplierSections({ sections, editable, showQuantity, onChanged, onOpenCatalog }: { sections: SupplierSection[]; editable: boolean; showQuantity: boolean; onChanged: () => void; /** Ctrl+klik (Windows: Alt+klik): the supplier's position in its catalog */ onOpenCatalog?: ((t: CatalogTarget, name: string) => void) | undefined }) {
  // owner 2026-10-23 20:49: the supplier's lines carry its colour stripe (grey without one); screen only, not in the PDF
  const colors = useContractorColors();
  if (sections.length === 0) return null;
  return (
    <div className="pl-suppliers" data-feature-id="packlists.suppliers">
      {sections.map((s) => (
        <section key={s.supplierId} className="pl-supplier" aria-label={`Odbiór: ${s.supplierName}`} style={markStyle(colors.get(s.supplierId))}>
          <h3>Odbiór: {s.supplierName}</h3>
          <table className="table">
            <tbody>
              {s.lines.map((l, i) => (
                <CatalogRow key={`${l.quoteLineId ?? l.name}-${i}`} open={onOpenCatalog ? () => onOpenCatalog(l.itemId ? { kind: 'supplier', supplierId: s.supplierId, itemId: l.itemId } : { kind: 'supplier', supplierId: s.supplierId, name: l.name }, l.name) : undefined}>
                  <td title={onOpenCatalog ? catalogTitle() : undefined}>{l.name}</td>
                  {showQuantity && <td className="num">{l.quantity ?? ''}</td>}
                  <td className="pl-supplier-pick">{editable && l.quoteLineId ? <SupplierPick line={l} supplierId={s.supplierId} supplierName={s.supplierName} onChanged={onChanged} /> : null}</td>
                </CatalogRow>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

/** A supplier's line placed among our gear: the line and its supplier. */
export type PlacedSupplierLine = { line: SupplierLine; supplierId: string; supplierName: string };

/**
 * Owner 2026-10-24 („mialo byc jak w kosztorysie … nic nie ma ladowac na dole”): which category group of the packlist a
 * supplier's line stands in — its position's category, else the quote section's it came from, else our uncategorised
 * gear's ('') — only a category the sheet knows, as for our own line.
 */
export function supplierGroupKey(line: Pick<SupplierLine, 'categoryId' | 'sectionCategoryId'>, known: ReadonlySet<string>): string {
  if (line.categoryId && known.has(line.categoryId)) return line.categoryId;
  if (line.sectionCategoryId && known.has(line.sectionCategoryId)) return line.sectionCategoryId;
  return '';
}

/**
 * A supplier's line in the packlist's table, marked as in the quote: the supplier's colour stripe at the name (softer,
 * #169) and the supplier's name in its colour (screen only; the PDF keeps „Odbiór: <dostawca>”). Nothing to reserve, no
 * window of ours: its rental is in „Dopożyczenia” (on hover). With `editable` the supplier of a quote's line is changed
 * the quote's way.
 */
export function SupplierLineRow({ placed, sectionCell, cols, editable, supplierEditable, onChanged, onOpenCatalog, onRemove }: {
  placed: PlacedSupplierLine;
  sectionCell: ReactNode;
  cols: { select: boolean; quantity: boolean; window: boolean; notes: boolean };
  /** the sheet is editable (its actions column is there) */
  editable: boolean;
  supplierEditable: boolean;
  onChanged: () => void;
  onOpenCatalog?: ((t: CatalogTarget, name: string) => void) | undefined;
  /** Owner 2026-10-24 20:36: its × (only for whoever may — `SupplierRemoveDialog` asks what to delete). */
  onRemove?: (() => void) | undefined;
}) {
  const colors = useContractorColors();
  const { line: l, supplierId, supplierName } = placed;
  const hover = `Odbiór u dostawcy ${supplierName}${l.quantity !== undefined ? `: ${l.quantity} szt.` : ''} — dopożyczenie, koszt i zwrot w zakładce „Dopożyczenia”. Nie rezerwuje naszego magazynu.`;
  const open = onOpenCatalog ? () => onOpenCatalog(l.itemId ? { kind: 'supplier', supplierId, itemId: l.itemId } : { kind: 'supplier', supplierId, name: l.name }, l.name) : undefined;
  return (
    <CatalogRow className={`sheet-row pl-row pl-from-supplier${l.rentalMark?.kind === 'anulowane' ? ' pl-rental-cancelled' : ''}`} style={markStyle(colors.get(supplierId))} title={hover} data-supplier-id={supplierId} open={open}>
      <th scope="row" className="sheet-section-cell">
        {sectionCell}
      </th>
      {cols.select && <td className="pl-check" />}
      <td className="sheet-main pl-name" title={onOpenCatalog ? catalogTitle() : undefined}>
        <span className="pl-stripe" aria-hidden="true" />
        <span className="pl-item">{l.name}</span>
        <span className="pl-sub" data-feature-id="packlists.suppliers">
          Odbiór: <span className="pl-supplier-name">{supplierName}</span>
        </span>
        {/* owner 2026-11-01: the rental of a closed event — „Anulowano – wydarzenie odwołane” (struck through) or the warning */}
        {l.rentalMark && <span className={`pl-sub rental-mark rental-mark-${l.rentalMark.kind}`}>{l.rentalMark.label}</span>}
      </td>
      {cols.quantity && (
        <td className="num pl-qty" data-label="Ilość">
          {l.quantity ?? ''}
        </td>
      )}
      {cols.window && <td className="pl-window" data-label="Okno" />}
      <td className="pl-res" data-label="Rezerwacja">
        {supplierEditable && l.quoteLineId ? <SupplierPick line={l} supplierId={supplierId} supplierName={supplierName} onChanged={onChanged} /> : <span className="muted">u dostawcy</span>}
      </td>
      {cols.notes && <td className="sheet-note pl-notes" />}
      {editable && (
        <td className="q-actions">
          {onRemove && (
            <button className="btn icon ghost q-remove" type="button" aria-label={`Usuń ze sprzętówki: ${l.name}`} title="Usuń ze sprzętówki" onClick={onRemove}>
              ×
            </button>
          )}
        </td>
      )}
    </CatalogRow>
  );
}

type Choice = { supplierId: string; supplierName: string; priceDayGrosze: number | null };

/** The quote line's suppliers (with their price per day), as in the quote's "Dostawca"; a change goes through the quote. */
function SupplierPick({ line, supplierId, supplierName, onChanged }: { line: SupplierLine; supplierId: string; supplierName: string; onChanged: () => void }) {
  const [data, setData] = useState<{ suppliers: Choice[]; version: number } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** Owner 2026-10-23 20:59: the chosen supplier has no position of this item — said so, nothing changed. */
  const [lacking, setLacking] = useState<{ supplierId: string; supplierName: string } | null>(null);
  /** Read again after a 409 (review): the line's version and suppliers as they are now. */
  const [reads, setReads] = useState(0);
  useEffect(() => {
    let alive = true;
    api<{ suppliers: Choice[]; version: number }>(`/api/quotes/lines/${line.quoteLineId}/suppliers`)
      .then((r) => alive && setData(r))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, [line.quoteLineId, supplierId, reads]);
  if (!data) return null;
  const list = data.suppliers.some((c) => c.supplierId === supplierId) ? data.suppliers : [{ supplierId, supplierName, priceDayGrosze: null }, ...data.suppliers];
  async function move(next: string) {
    if (next === supplierId || !data) return;
    setBusy(true);
    setLacking(null);
    try {
      await api(`/api/quotes/lines/${line.quoteLineId}/supplier`, { method: 'PATCH', body: { supplierId: next, version: data.version } });
      setError('');
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'supplier_lacks_item') {
        setError('');
        setLacking({ supplierId: next, supplierName: list.find((c) => c.supplierId === next)?.supplierName ?? '' });
        return;
      }
      setError(e instanceof ApiError ? (e.status === 409 ? 'Ktoś w tym czasie zmienił tę pozycję w kosztorysie. Pokazuję aktualne wartości.' : (fieldIssues(e)[0]?.message ?? e.message)) : 'Nie udało się zmienić dostawcy');
      if (e instanceof ApiError && e.status === 409) {
        setReads((n) => n + 1);
        onChanged();
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <select className="cell-input" aria-label={`Dostawca: ${line.name}`} value={supplierId} disabled={busy} onChange={(e) => void move(e.target.value)}>
        {list.map((c) => (
          <option key={c.supplierId} value={c.supplierId}>
            {c.priceDayGrosze !== null ? `${c.supplierName} · ${formatPln(c.priceDayGrosze)} / doba` : c.supplierName}
          </option>
        ))}
      </select>
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
      {lacking && (
        <span className="field-error" role="alert">
          <SupplierLacksItem supplierId={lacking.supplierId} supplierName={lacking.supplierName} itemId={line.itemId} />
        </span>
      )}
    </>
  );
}

/**
 * "Utwórz sprzętówkę z kosztorysu…" in the packlist's "…": the quote's versions — one is used at once, several ask which
 * (the governing one chosen). A packlist with anything gets a new version (the server says so).
 */
export function useFromQuote(me: Me | undefined, eventId: string, enabled: boolean, onNotice: (text: string) => void): { action: RowAction | null; view: ReactNode } {
  const queryClient = useQueryClient();
  const allowed = enabled && !!me && packlistRights(me).edit && can(me, QUOTES_RESOURCE, 'view');
  const quote = useQuery({ queryKey: quoteKey(eventId), enabled: allowed, retry: false, queryFn: () => api<QuoteOfEvent>(`/api/quotes/events/${eventId}`) });
  const [choosing, setChoosing] = useState<string | null>(null);
  const make = useMutation({
    // gear other events reserved first: one „Dodaj mimo to” / „Anuluj” for all of it (owner 2026-10-24)
    mutationFn: async (v: VersionSummary) => ({
      result: await withConflictConfirm((confirmConflicts) => api<FromQuoteResult>(`/api/quotes/versions/${v.id}/to-packlist`, { method: 'POST', body: confirmConflicts ? { confirmConflicts } : {} })),
      number: v.number,
    }),
    onSuccess: ({ result, number }) => {
      setChoosing(null);
      if (result) onNotice(fromQuoteText(result, number));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
      void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
    },
  });
  const versions = quote.data?.quote && !quote.data.heldByOther ? quote.data.versions : [];
  if (!allowed || versions.length === 0) return { action: null, view: null };
  const start = () => {
    make.reset();
    // the last notice goes: this action's own outcome comes next (review)
    onNotice('');
    if (versions.length === 1) make.mutate(versions[0]!);
    else setChoosing(defaultQuoteVersion(versions)!.id);
  };
  const error = make.error ? (make.error instanceof ApiError ? make.error.message : 'Nie udało się utworzyć sprzętówki') : '';
  const view = (
    <>
      {error && !choosing && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
      {choosing && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="pl-from-quote-title">
            <DialogLayer onClose={() => setChoosing(null)} busy={make.isPending} />
            <h2 id="pl-from-quote-title">Utwórz sprzętówkę z kosztorysu</h2>
            <p className="muted">Gdy sprzętówka już coś ma, powstanie jej nowa wersja — obecna zostaje w wersjach.</p>
            <fieldset className="pl-from-quote">
              <legend>Wersja kosztorysu</legend>
              {[...versions]
                .sort((a, b) => a.number - b.number)
                .map((v) => (
                  <label key={v.id}>
                    <input type="radio" name="pl-from-quote" checked={choosing === v.id} onChange={() => setChoosing(v.id)} /> v{v.number} · {stateText(v)}
                  </label>
                ))}
            </fieldset>
            {error && (
              <p className="alert" role="alert">
                {error}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="button" disabled={make.isPending} onClick={() => make.mutate(versions.find((v) => v.id === choosing)!)}>
                {make.isPending ? 'Tworzenie…' : 'Utwórz'}
              </button>
              <button className="btn" type="button" onClick={() => setChoosing(null)}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
  return { action: { label: 'Utwórz sprzętówkę z kosztorysu…', onSelect: start }, view };
}

/** "Usuń wersję" and "Przywróć jako aktualną": the answer is the packlist as it is now. */
export function useVersionActions(eventId: string, onDone: (text: string, view: PacklistView) => void, onStart: () => void = () => undefined) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const run = useMutation({
    mutationFn: async (a: { kind: 'delete' | 'restore'; number: number; current: number }) => {
      const view =
        a.kind === 'delete'
          ? await api<PacklistView>(`/api/packlists/${eventId}/versions/${a.number}?current=${a.current}`, { method: 'DELETE' })
          : await withConflictConfirm((confirmConflicts) => api<PacklistView>(`/api/packlists/${eventId}/versions/${a.number}/restore`, { method: 'POST', body: { current: a.current, ...(confirmConflicts ? { confirmConflicts } : {}) } }));
      return { a, view };
    },
    onMutate: () => {
      // the last notice and error go: this action's own outcome comes next (review)
      setError('');
      onStart();
    },
    onSuccess: ({ a, view }) => {
      setError('');
      // „Anuluj” in „Dodaj mimo to”: nothing restored
      if (!view) return;
      queryClient.setQueryData(packlistKey(eventId), view);
      const now = view.currentVersion ?? 1;
      onDone(
        a.kind === 'restore'
          ? `Przywrócono wersję v${a.number} jako aktualną (v${now}).`
          : a.number === a.current
            ? `Usunięto wersję v${a.number} sprzętówki. Aktualna jest teraz v${now}.`
            : `Usunięto wersję v${a.number} sprzętówki.`,
        view,
      );
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setError('Ktoś w tym czasie zmienił wersje sprzętówki. Pokazuję aktualne.');
        void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
      } else setError(e instanceof ApiError ? e.message : 'Nie udało się zmienić wersji sprzętówki');
    },
  });
  return { run: (a: { kind: 'delete' | 'restore'; number: number; current: number }) => run.mutate(a), busy: run.isPending, error };
}

/**
 * Review I2: a pickup already picked up differs from the quote — it was not changed by itself. "Uzupełnij odbiory" makes
 * the supplier gear as the quote's governing version has it (the quote's rights: edit the packlist, see the quote).
 */
export function PickupsDiffer({ me, eventId, differs, follows = true }: { me: Me; eventId: string; differs: boolean; /** Review M4: false — the quote was deleted. */ follows?: boolean }) {
  const queryClient = useQueryClient();
  const sync = useMutation({
    mutationFn: () => api(`/api/quotes/events/${eventId}/pickups`, { method: 'POST', body: {} }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) }),
  });
  if (!differs) return null;
  if (!follows) {
    return (
      <p className="notice pl-pickups-differ" role="status" data-feature-id="packlists.suppliers">
        Kosztorys tego wydarzenia usunięto — odbiory zostały, sprawdź je w „Dopożyczeniach”.
      </p>
    );
  }
  // review M2: as the server — edit the packlist and see (so write) the quantities of the packlist and the quote
  const may = packlistRights(me).edit && can(me, QUOTES_RESOURCE, 'view');
  return (
    <p className="notice pl-pickups-differ" role="status" data-feature-id="packlists.suppliers">
      Odbiór różni się od kosztorysu — sprzęt jest już odebrany, więc nie zmienił się sam.{' '}
      {may && (
        <button className="btn small" type="button" disabled={sync.isPending} onClick={() => sync.mutate()}>
          Uzupełnij odbiory
        </button>
      )}
      {sync.error && <span className="field-error"> {sync.error instanceof ApiError ? sync.error.message : 'Nie udało się'}</span>}
    </p>
  );
}
